import { useMemo } from 'react';
import { useResponseParsing } from './useResponseParsing';

export interface ExtractedTask {
  title: string;
  description: string;
  goalId?: string;
  confidence: number;
}

export interface ExtractedResearch {
  title: string;
  description: string;
  goalId?: string;
  confidence: number;
}

export interface ExtractedPage {
  title: string;
  description: string;
  type: string;
  projectId?: string;
  confidence: number;
}

export interface ExtractedImage {
  description: string;
  style: string;
  size: string;
  projectId?: string;
  confidence: number;
}

export interface ExtractedProject {
  title: string;
  description: string;
  type: string;
  confidence: number;
}

export interface RecommendationDetectionResult {
  hasRecommendations: boolean;
  tasks: ExtractedTask[];
  research: ExtractedResearch[];
  pages: ExtractedPage[];
  images: ExtractedImage[];
  projects: ExtractedProject[];
  hasContentGeneration: boolean;
  reason?: string;
}

/**
 * Hook to detect multiple recommendation types in AI messages
 * Now enhanced with ID extraction for global smart detection
 */
export const useRecommendationDetection = (messageContent: string): RecommendationDetectionResult => {
  const { parseRecommendationResponse } = useResponseParsing();

  return useMemo(() => {
    if (!messageContent || messageContent.trim().length === 0) {
      return { 
        hasRecommendations: false, 
        tasks: [], 
        research: [], 
        pages: [],
        images: [],
        projects: [],
        hasContentGeneration: false,
        reason: 'Empty content' 
      };
    }

    const content = messageContent.trim();
    const tasks: ExtractedTask[] = [];
    const research: ExtractedResearch[] = [];
    const pages: ExtractedPage[] = [];
    const images: ExtractedImage[] = [];
    const projects: ExtractedProject[] = [];

    // Parse the response using the new parsing system
    const parseResult = parseRecommendationResponse(content);
    
    if (parseResult.hasRecommendation && parseResult.recommendation) {
      const rec = parseResult.recommendation;
      
      // Only process valid recommendations
      if (rec.isValid) {
        switch (rec.type) {
          case 'task':
            tasks.push({
              title: rec.title,
              description: rec.description,
              goalId: rec.goalId,
              confidence: 0.95
            });
            break;
            
          case 'research':
            research.push({
              title: rec.title,
              description: rec.description,
              goalId: rec.goalId,
              confidence: 0.95
            });
            break;
            
          case 'page':
            pages.push({
              title: rec.title,
              description: rec.description,
              type: rec.pageType || 'Other',
              projectId: rec.projectId,
              confidence: 0.95
            });
            break;
            
          case 'image':
            images.push({
              description: rec.description,
              style: rec.imageStyle || 'photo',
              size: rec.imageSize || '1024x1024',
              projectId: rec.projectId,
              confidence: 0.95
            });
            break;
            
          case 'project':
            projects.push({
              title: rec.title,
              description: rec.description,
              type: rec.projectType || 'Other',
              confidence: 0.95
            });
            break;
        }
      } else {
        // Log validation errors for debugging
        console.warn('[RecommendationDetection] Invalid recommendation:', rec.errors);
        console.warn('[RecommendationDetection] Full recommendation object:', rec);
        console.warn('[RecommendationDetection] Original message content:', content);
      }
    }

    // Check for content generation trigger
    const hasContentGeneration = content.includes("OPEN AI TEXT MAGIC");
    
    const hasRecommendations = tasks.length > 0 || research.length > 0 || pages.length > 0 || images.length > 0 || projects.length > 0;
    
    let reason = '';
    if (hasRecommendations) {
      if (tasks.length > 0) {
        reason = `Detected 1 task recommendation${tasks[0].goalId ? ' with goalId' : ''}`;
      } else if (research.length > 0) {
        reason = `Detected 1 research recommendation${research[0].goalId ? ' with goalId' : ''}`;
      } else if (pages.length > 0) {
        reason = hasContentGeneration 
          ? `Detected 1 page recommendation with content generation${pages[0].projectId ? ' and projectId' : ''}` 
          : `Detected 1 page recommendation${pages[0].projectId ? ' with projectId' : ''}`;
      } else if (images.length > 0) {
        reason = `Detected 1 image recommendation${images[0].projectId ? ' with projectId' : ''}`;
      } else if (projects.length > 0) {
        reason = `Detected 1 project recommendation`;
      }
    } else if (parseResult.hasRecommendation && !parseResult.recommendation?.isValid) {
      reason = `Invalid recommendation format: ${parseResult.recommendation?.errors.join(', ')}`;
    } else {
      reason = 'No explicit recommendation format found';
    }

    return {
      hasRecommendations,
      tasks,
      research,
      pages,
      images,
      projects,
      hasContentGeneration,
      reason
    };
  }, [messageContent, parseRecommendationResponse]);
};

/**
 * Backward compatibility hook - returns first task if any exist
 */
export const useTaskDetection = (messageContent: string) => {
  const { hasRecommendations, tasks } = useRecommendationDetection(messageContent);
  
  return useMemo(() => {
    if (tasks.length > 0) {
      return {
        isTask: true,
        extractedTask: tasks[0],
        reason: 'Task detected via recommendation system'
      };
    }
    
    return {
      isTask: false,
      reason: 'No tasks detected'
    };
  }, [hasRecommendations, tasks]);
}; 